import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "../../styles/pages/home/Leadership.module.scss";

export default function Leadership({
  items = defaultItems,
  autoPlay = true,
  interval = 4500,
}) {
  const viewportRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const safeItems = useMemo(
    () => (Array.isArray(items) && items.length ? items : defaultItems),
    [items]
  );
  // Snap to slide by index
  const goTo = (i) => {
    if (!viewportRef.current) return;
    const vp = viewportRef.current;
    const clamped = Math.max(0, Math.min(i, safeItems.length - 1));
    const target = clamped * vp.clientWidth; // each snap ≈ viewport width
    vp.scrollTo({ left: target, behavior: "smooth" });
    setIndex(clamped);
  };

  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  // Keep index in sync with scroll (drag / wheel / touch)
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    let rAF = null;
    const onScroll = () => {
      cancelAnimationFrame(rAF);
      rAF = requestAnimationFrame(() => {
        const newIndex = Math.round(vp.scrollLeft / vp.clientWidth);
        if (newIndex !== index) setIndex(newIndex);
      });
    };

    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      vp.removeEventListener("scroll", onScroll);
      if (rAF) cancelAnimationFrame(rAF);
    };
  }, [index]);

  // Autoplay (pause on hover/focus/drag)
  useEffect(() => {
    if (!autoPlay || isUserInteracting) return;
    const id = setInterval(() => {
      setIndex((i) => {
        const nextIndex = (i + 1) % safeItems.length;
        goTo(nextIndex);
        return nextIndex;
      });
    }, interval);
    return () => clearInterval(id);
  }, [autoPlay, interval, safeItems.length, isUserInteracting]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <section className={styles.leadership} aria-label="Leadership highlights">
      {/* Diagonal frame */}
      <div className={styles.skewWrap}>
        {/* Unskew content so it appears level */}
        <div className={styles.unskew}>
          {/* Controls */}
          <div className={styles.headerRow}>
            <h2 className={styles.sectionTitle}>Community</h2>
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.navBtn}
                onClick={prev}
                aria-label="Previous highlight"
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.navBtn}
                onClick={next}
                aria-label="Next highlight"
              >
                ›
              </button>
            </div>
          </div>

          {/* Viewport */}
          <div
            className={styles.viewport}
            ref={viewportRef}
            onPointerDown={() => setIsUserInteracting(true)}
            onPointerUp={() => setIsUserInteracting(false)}
            onMouseEnter={() => setIsUserInteracting(true)}
            onMouseLeave={() => setIsUserInteracting(false)}
            role="region"
            aria-roledescription="carousel"
            aria-label="Leadership carousel"
            aria-live="polite"
          >
            <div className={styles.track}>
              {safeItems.map((item, i) => {
                const active = i === index;
                return (
                  <article
                    key={item.id || i}
                    className={`${styles.card} ${active ? styles.active : ""}`}
                    aria-roledescription="slide"
                    aria-label={`${item.title} – ${item.subtitle}`}
                    aria-current={active ? "true" : "false"}
                    style={
                      item.accent
                        ? { "--accent": item.accent }
                        : undefined
                    }
                  >
                    {/* Background / hero */}
                    <div
                      className={styles.media}
                      style={
                        item.image
                          ? { backgroundImage: `url(${item.image})` }
                          : undefined
                      }
                    />

                    {/* Logo */}
                    {item.logo && (
                      <div className={styles.logoWrap}>
                        <img
                          src={item.logo}
                          alt={`${item.title} logo`}
                          className={styles.logo}
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Meta */}
                    <div className={styles.meta}>
                      <div className={styles.textBlock}>
                        <span className={styles.subtitle}>{item.subtitle}</span>
                        <h3 className={styles.cardTitle}>{item.title}</h3>
                      </div>
                      {Array.isArray(item.tags) && item.tags.length > 0 && (
                        <ul className={styles.tags} aria-label="Tags">
                          {item.tags.map((t, k) => (
                            <li className={styles.tag} key={k}>
                              {t}
                            </li>
                          ))}
                        </ul>
                      )}
                      {item.href && (
                        <a
                          href={item.href}
                          className={styles.cta}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`Open ${item.title} in a new tab`}
                        >
                          Learn more
                        </a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Dots */}
          <div className={styles.dots} role="tablist" aria-label="Slide dots">
            {safeItems.map((_, i) => (
              <button
                key={i}
                className={`${styles.dot} ${i === index ? styles.dotActive : ""}`}
                onClick={() => goTo(i)}
                role="tab"
                aria-selected={i === index}
                aria-controls={`slide-${i}`}
                tabIndex={i === index ? 0 : -1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

Leadership.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      image: PropTypes.string,
      logo: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
      href: PropTypes.string,
      accent: PropTypes.string,
    })
  ),
  autoPlay: PropTypes.bool,
  interval: PropTypes.number,
};

/* ---------- Example data (delete or replace) ---------- */
const defaultItems = [
  {
    id: "google-wtm",
    title: "Google Women Techmakers",
    subtitle: "Ambassador",
    image: "/images/wtm.jpg",
    logo: "https://miro.medium.com/v2/1*d7pBKQ_eDm0BBwTE6p9ePA.jpeg",
    tags: ["Equity", "Inclusion", "STEM"],
    href: "https://women.google/techmakers/",
    accent: "#7C4DFF",
  },
  {
    id: "hack-for-la",
    title: "Hack for LA",
    subtitle: "Civic Tech Contributor",
    image: "/images/hack4la.jpg",
    logo: "/logos/hackforla.svg",
    tags: ["Civic Tech", "Open Source"],
    href: "https://www.hackforla.org/",
    accent: "#8E24AA",
  },
  {
    id: "red-cross",
    title: "American Red Cross",
    subtitle: "Volunteer",
    image: "/images/redcross.jpg",
    logo: "/logos/redcross.svg",
    tags: ["Disaster Relief", "Community"],
    href: "https://www.redcross.org/",
    accent: "#D32F2F",
  },
  {
    id: "un-collab",
    title: "United Nations",
    subtitle: "Collaborator",
    image: "/images/un.jpg",
    logo: "/logos/un.svg",
    tags: ["Global Impact", "Policy"],
    href: "https://www.un.org/",
    accent: "#1473E6",
  },
];
